import React, { useState, useEffect } from "react";
import {
  Table, Modal, Form, Input, DatePicker, Button,
  message, Popconfirm, Tag, Space, Input as AntInput,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

// ── Hook de responsividade (mesmo padrão usado no Dashboard) ────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

const ROTA_PESQUISAR = "/ServerPrincipal/PesquisaPassaporte";
const ROTA_INSERIR   = "/ServerPrincipal/InserirPassaporte";
const ROTA_ATUALIZAR = "/ServerPrincipal/AtualizarPassaporte";
const ROTA_DELETAR   = "/ServerPrincipal/DeletarPassaporte";


const DIAS_ALERTA_VENCENDO = 180;

const FORMATO_DATA = "DD/MM/YYYY";


const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

const parseDataSegura = (valor) => {
  if (!valor) return null;
  const tentativas = [FORMATO_DATA, "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"];
  for (const fmt of tentativas) {
    const d = dayjs(valor, fmt, true);
    if (d.isValid()) return d;
  }
  const fallback = dayjs(valor);
  return fallback.isValid() ? fallback : null;
};

const mapPassaporte = (row) => ({
  id:         row.por_id,
  nome:       row.por_nome ?? "",
  passaporte: row.por_passaporte ?? "",
  vencimento: row.por_dtvencimento ?? "",
});

// Classifica o status de vencimento a partir da data.
const statusVencimento = (dataVencimento) => {
  const d = parseDataSegura(dataVencimento);
  if (!d) return { label: "Sem data", cor: "default", dias: null };

  const hoje = dayjs().startOf("day");
  const dias = d.startOf("day").diff(hoje, "day");

  if (dias < 0)                        return { label: "Vencido", cor: "red", dias };
  if (dias <= DIAS_ALERTA_VENCENDO)    return { label: "Vencendo em breve", cor: "orange", dias };
  return { label: "Válido", cor: "green", dias };
};

export const Passaporte = ({ clienteId: clienteIdProp }) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [clienteId, setClienteId]     = useState(null);
  const [passaportes, setPassaportes] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando]       = useState(null); // null = criando novo
  const [busca, setBusca]             = useState("");

  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
    }
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) { setClienteId(id); carregarPassaportes(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  const carregarPassaportes = async (id) => {
    setLoading(true);
    try {
      const res  = await api.get(ROTA_PESQUISAR, { params: { Login: id } });
      const rows = extractRows(res.data).map(normalize).map(mapPassaporte);
      setPassaportes(rows);
    } catch (e) {
      message.error("Erro ao carregar passaportes.");
      console.error("[Passaporte] carregarPassaportes →", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirNovo = () => {
    setEditando(null);
    form.resetFields();
    setModalAberto(true);
  };

  const abrirEdicao = (registro) => {
    setEditando(registro);
    form.setFieldsValue({
      nome: registro.nome,
      passaporte: registro.passaporte,
      vencimento: parseDataSegura(registro.vencimento),
    });
    setModalAberto(true);
  };

  const handleSalvar = async (values) => {
    setSaving(true);
    try {
      const payload = {
        Nome:       values.nome,
        Passaporte: values.passaporte,
        Vencimento: values.vencimento.format(FORMATO_DATA),
      };

      if (editando) {
        await api.post(ROTA_ATUALIZAR, { ...payload, IDPassaporte: editando.id });
        message.success("Passaporte atualizado com sucesso!");
      } else {
        await api.post(ROTA_INSERIR, payload);
        message.success("Passaporte cadastrado com sucesso!");
      }

      setModalAberto(false);
      form.resetFields();
      carregarPassaportes(clienteId);
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao salvar passaporte";
      message.error(String(errMsg));
      console.error("[Passaporte] handleSalvar →", e?.response?.status, e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (registro) => {
    const anteriores = passaportes;
    setPassaportes((prev) => prev.filter((p) => p.id !== registro.id)); // otimista
    try {
      await api.post(ROTA_DELETAR, { IDPassaporte: registro.id });
      message.success("Passaporte excluído.");
    } catch (e) {
      message.error("Não foi possível excluir o passaporte.");
      setPassaportes(anteriores);
      console.error("[Passaporte] handleExcluir →", e?.response?.data || e?.message);
    }
  };

  const passaportesFiltrados = passaportes
    .filter((p) => {
      if (!busca) return true;
      const alvo = busca.toLowerCase();
      return p.nome.toLowerCase().includes(alvo) || p.passaporte.toLowerCase().includes(alvo);
    })
    .sort((a, b) => {
      const da = parseDataSegura(a.vencimento);
      const db = parseDataSegura(b.vencimento);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.valueOf() - db.valueOf();
    });

  const totalVencidos  = passaportes.filter((p) => statusVencimento(p.vencimento).label === "Vencido").length;
  const totalVencendo  = passaportes.filter((p) => statusVencimento(p.vencimento).label === "Vencendo em breve").length;

  const colunas = [
    {
      title: "Nome",
      dataIndex: "nome",
      key: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      // largura mínima explícita: sem isso, o antd Table esmaga as colunas
      // até ficarem ilegíveis em telas estreitas em vez de rolar horizontal.
      width: 160,
    },
    {
      title: "Passaporte",
      dataIndex: "passaporte",
      key: "passaporte",
      width: 130,
    },
    {
      title: "Vencimento",
      dataIndex: "vencimento",
      key: "vencimento",
      width: 120,
      render: (v) => {
        const d = parseDataSegura(v);
        return d ? d.format(FORMATO_DATA) : "—";
      },
    },
    {
      title: "Status",
      key: "status",
      width: 170,
      render: (_, registro) => {
        const st = statusVencimento(registro.vencimento);
        const sufixo = st.dias === null
          ? ""
          : st.dias < 0
            ? ` (há ${Math.abs(st.dias)}d)`
            : ` (${st.dias}d)`;
        return <Tag color={st.cor}>{st.label}{sufixo}</Tag>;
      },
      filters: [
        { text: "Vencido", value: "Vencido" },
        { text: "Vencendo em breve", value: "Vencendo em breve" },
        { text: "Válido", value: "Válido" },
      ],
      onFilter: (value, registro) => statusVencimento(registro.vencimento).label === value,
    },
    {
      title: "Ações",
      key: "acoes",
      width: 100,
      fixed: isMobile ? "right" : false,
      render: (_, registro) => (
        <Space>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => abrirEdicao(registro)} />
          <Popconfirm
            title="Excluir este passaporte?"
            okText="Excluir" cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleExcluir(registro)}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const s = {
    wrap:    { padding: isMobile ? "16px 12px" : "20px 24px" },
    header:  { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 },
    titulo:  { fontSize: 18, fontWeight: 700, color: "#222", margin: 0 },
    resumo:  { display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" },
    resumoCard: (cor) => ({
      background: "#fff", border: `1px solid ${cor}33`, borderLeft: `3px solid ${cor}`,
      borderRadius: 6, padding: "8px 14px", fontSize: 12, color: "#555",
      flex: isMobile ? "1 1 100%" : "initial",
    }),
    resumoNum: (cor) => ({ fontSize: 18, fontWeight: 700, color: cor }),
    toolbar: { display: "flex", justifyContent: "space-between", marginBottom: 12, gap: 10, flexWrap: "wrap" },
  };

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <h2 style={s.titulo}>Passaportes</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={abrirNovo}
          style={{ background: "#000000", borderColor: "#000000" }}
        >
          Novo Passaporte
        </Button>
      </div>

      <div style={s.resumo}>
        <div style={s.resumoCard("#ff4d4f")}>
          <div style={s.resumoNum("#ff4d4f")}>{totalVencidos}</div>
          Vencido{totalVencidos !== 1 ? "s" : ""}
        </div>
        <div style={s.resumoCard("#fa8c16")}>
          <div style={s.resumoNum("#fa8c16")}>{totalVencendo}</div>
          Vencendo nos próximos {DIAS_ALERTA_VENCENDO} dias
        </div>
        <div style={s.resumoCard("#52c41a")}>
          <div style={s.resumoNum("#52c41a")}>{passaportes.length}</div>
          Total cadastrados
        </div>
      </div>

      <div style={s.toolbar}>
        <AntInput
          placeholder="Buscar por nome ou número do passaporte"
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{ maxWidth: isMobile ? "100%" : 320, width: isMobile ? "100%" : "auto" }}
          allowClear
        />
      </div>

      <Table
        rowKey="id"
        columns={colunas}
        dataSource={passaportesFiltrados}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        locale={{ emptyText: "Nenhum passaporte cadastrado." }}
        // Em vez de deixar o antd esmagar as 5 colunas até ficarem ilegíveis
        // em telas pequenas, a tabela vira scrollável horizontalmente e a
        // coluna de Ações fica fixa (fixed: "right" acima) pra sempre estar
        // acessível sem precisar rolar até o fim.
        scroll={{ x: 680 }}
        size={isMobile ? "small" : "middle"}
      />

      <Modal
        title={editando ? "Editar Passaporte" : "Novo Passaporte"}
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editando ? "Salvar" : "Cadastrar"}
        cancelText="Cancelar"
        destroyOnClose
        width={isMobile ? "92vw" : 520}
      >
        <Form form={form} layout="vertical" onFinish={handleSalvar}>
          <Form.Item name="nome" label="Nome do titular" rules={[{ required: true, message: "Informe o nome." }]}>
            <Input placeholder="Ex: João da Silva" />
          </Form.Item>
          <Form.Item name="passaporte" label="Número do Passaporte" rules={[{ required: true, message: "Informe o número do passaporte." }]}>
            <Input placeholder="Ex: FZ123456" style={{ textTransform: "uppercase" }} />
          </Form.Item>
          <Form.Item name="vencimento" label="Data de Vencimento" rules={[{ required: true, message: "Informe a data de vencimento." }]}>
            <DatePicker style={{ width: "100%" }} format={FORMATO_DATA} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Passaporte;